import { useUserPermissions } from '@/hooks/useUserPermissions';
import { tabKeyboard } from '@/lib/tabKeyboard';
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { v2, type DocumentTemplate } from '@/api/v2Client';
import { formatApiError } from '@/lib/errors';
import {
  renderTemplatePreview,
  regionOfTemplate,
  countTemplateDirectives,
} from '@/lib/templateRender';
import { RESIZE_GRIP_STYLE, useDraggableModal } from '@/hooks/useDraggableModal';

/**
 * Phase phase-form-templates-ui-fixes (FT-5) — read-only Preview modal.
 *
 * V1's FormTemplatesScreen let you preview a template's rendered content
 * before editing; V2 dropped it in the parity migration. This restores a
 * read-only view: template name + description + the rendered body with
 * `{{variable}}` placeholders shown as `[VARIABLE]` so the reader sees the
 * actual template text. No editing — "let me read this before deciding to
 * use or edit it."
 */
export interface TemplatePreviewModalProps {
  readonly template: DocumentTemplate;
  readonly onClose: () => void;
  /**
   * phase-small-findings-batch-1 (item 4) — open already scoped to a company
   * profile. The Company Profiles branding section previews a cover letter as
   * THAT company without making the reader re-pick it in "Preview as". Omit
   * (the Form Templates screen does) to open in sample mode, unchanged.
   */
  readonly initialProfileId?: string;
}

type PreviewTab = 'text' | 'pdf';

/**
 * phase-ui-template-pdf-preview — fetch the rendered preview PDF as an object
 * URL for the tab's iframe.
 *
 * The PDF is fetched through `apiFetch` (bearer token attached) and turned into
 * a blob URL rather than pointing the iframe at the API path directly — a bare
 * iframe GET carries no Authorization header and would 401 wherever auth is on.
 * The object URL is revoked on unmount / template change so we don't leak it.
 *
 * Only fetches once the PDF tab is actually opened: rendering costs a
 * server-side PDF build, and most readers only ever want the Text tab.
 */
function usePreviewPdfUrl(
  templateId: string,
  active: boolean,
  profileId: string | undefined,
): { url: string | null; loading: boolean; error: string | null; retry: () => void } {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // phase-fix-template-pdf-preview — bump to re-request after a failure (Retry).
  const [reloadNonce, setReloadNonce] = useState(0);
  const retry = useCallback(() => setReloadNonce((n) => n + 1), []);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    setLoading(true);
    setError(null);
    v2.documentTemplates
      .previewPdf(templateId, profileId)
      .then(async (res) => {
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // phase-fix-template-pdf-preview — the render route now returns the
        // safeErrorHandler shape ({error,message,correlationId}); apiFetch
        // throws an ApiError whose `.message` is the RAW response body, so
        // route it through formatApiError to surface the clean message (with
        // its "Reference: <id>" correlation id) and NEVER the raw JSON that
        // Sharon saw. formatApiError also filters any raw-DB-error text.
        setError(formatApiError(err, 'The PDF preview could not be rendered.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return (): void => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setUrl(null);
    };
  }, [templateId, active, reloadNonce, profileId]);

  return { url, loading, error, retry };
}

/**
 * phase-template-preview-company-profile — the Text tab's body. In sample mode
 * (no profile) the caller renders the template client-side (unchanged); with a
 * profile chosen this fetches the server-substituted text (the same resolver
 * the PDF uses), so both tabs show the profile's real values.
 */
function usePreviewText(
  templateId: string,
  active: boolean,
  profileId: string | undefined,
): { text: string | null; loading: boolean; error: string | null } {
  const [text, setText] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Only fetch when a profile is chosen AND the Text tab is showing; sample
    // mode uses the client-side render and needs no request.
    if (!active || !profileId) {
      setText(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    v2.documentTemplates
      .previewText(templateId, profileId)
      .then((res) => {
        if (!cancelled) setText(res.text);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(formatApiError(err, 'The preview could not be rendered.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return (): void => {
      cancelled = true;
    };
  }, [templateId, active, profileId]);

  return { text, loading, error };
}

export function TemplatePreviewModal({
  template,
  onClose,
  initialProfileId,
}: TemplatePreviewModalProps): ReactElement {
  const permissions = useUserPermissions();
  const canReadProfiles = permissions.hasPermission('CompanyProfiles', 'read');
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileReload, setProfileReload] = useState(0);
  const region = regionOfTemplate(template);
  const [tab, setTab] = useState<PreviewTab>('text');

  // phase-template-preview-company-profile — "Preview as": the tenant's own
  // Company Profiles, plus the default sample mode. Choosing a profile renders
  // BOTH tabs with that company's real values mapped in (submission-specific
  // values still «PLACEHOLDER»). '' = sample mode (unchanged behavior).
  const [profiles, setProfiles] = useState<{ Id: string; CompanyName: string }[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<string>(initialProfileId ?? '');
  useEffect(() => {
    if (!canReadProfiles) return;
    let cancelled = false;
    setProfileError(null);
    v2.companyProfiles
      .list()
      .then((rows) => {
        if (!cancelled) {
          setProfiles(rows.map((r) => ({ Id: r.Id, CompanyName: r.CompanyName })));
        }
      })
      .catch((reason: unknown) => {
        if (!cancelled) setProfileError(formatApiError(reason, 'Could not load company profiles. Sample preview is still available.'));
      });
    return (): void => {
      cancelled = true;
    };
  }, [canReadProfiles, profileReload]);
  const activeProfileId = canReadProfiles ? selectedProfileId || undefined : undefined;
  const selectedProfile = canReadProfiles ? profiles.find((p) => p.Id === selectedProfileId) ?? null : null;

  const pdf = usePreviewPdfUrl(template.Id, tab === 'pdf', activeProfileId);
  const textPreview = usePreviewText(template.Id, tab === 'text', activeProfileId);

  // The banner reflects the chosen mode; it heads BOTH tabs.
  //
  // phase-template-ai-directives — the old copy promised "layout, wrapping and
  // page breaks match" a generated form. That is FALSE for any template with
  // `[Generate …]` directives: each one expands into multi-paragraph drafted
  // prose at generation time, so the finished document is longer and paginates
  // differently. The preview does not run the model, so those blocks appear as
  // marked placeholders. Say so instead of over-promising.
  // Counted from the template body so the note is right in BOTH modes (sample
  // mode renders client-side and never calls the preview endpoint).
  const directiveCount = countTemplateDirectives(template.Body ?? '');
  const aiNote =
    directiveCount > 0
      ? ` This template has ${directiveCount} AI-drafted section${directiveCount === 1 ? '' : 's'}, shown as «placeholders» — the final wording and pagination will differ.`
      : ' Layout, wrapping and page breaks match a generated form.';
  const bannerText = selectedProfile
    ? `Previewing with ${selectedProfile.CompanyName}'s profile — submission-specific values shown as «PLACEHOLDER».${aiNote}`
    : `Sample data — every value shown as «PLACEHOLDER».${aiNote}`;
  // phase-ui-draggable-modals — drag the window by its title bar so it can be
  // moved aside to read the templates table underneath. Backdrop / overlay-click
  // / z-index are unchanged: this moves the window, it doesn't un-modal it.
  const drag = useDraggableModal();
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="template-preview-title"
      data-testid="template-preview-modal"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        ref={drag.windowRef}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          borderRadius: 12,
          width: '92%',
          // phase-ui-preview-modal-pdf-height-resize — a DEFINITE height (not
          // just a max) so flex:1 panes actually fill it: the PDF pane used to
          // collapse to its minHeight because the window's height was
          // content-driven. Both tabs now share this one footprint, so
          // switching tabs never changes the window's size. Width bumped
          // 720 → 860 for a comfortably readable PDF page (render-and-look
          // at 1440×900). The user's own resize (drag.windowStyle) overrides
          // all of this.
          maxWidth: 860,
          height: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
          overflow: 'hidden',
          ...drag.windowStyle,
        }}
      >
        <div
          data-testid="template-preview-titlebar"
          {...drag.handleProps}
          style={{
            background: '#1B3A6B',
            padding: '14px 20px',
            color: '#fff',
            ...drag.handleProps.style,
          }}
        >
          <div id="template-preview-title" style={{ fontSize: 16, fontWeight: 700 }}>
            {template.Name}
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>
            {[region, template.TargetModule].filter(Boolean).join(' · ') || 'Template preview'}
          </div>
        </div>

        {/* phase-ui-template-pdf-preview — Text | PDF toggle. Lives in the
            modal BODY; the frame/title bar is phase-ui-draggable-modals' and
            is left alone. */}
        <div
          role="tablist" onKeyDown={tabKeyboard}
          aria-label="Preview mode"
          style={{
            display: 'flex',
            gap: 4,
            padding: '10px 20px 0 20px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          {(
            [
              ['text', 'Text'],
              ['pdf', 'PDF'],
            ] as ReadonlyArray<readonly [PreviewTab, string]>
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key} tabIndex={(tab === key) ? 0 : -1}
              data-testid={`template-preview-tab-${key}`}
              onClick={() => setTab(key)}
              style={{
                border: 'none',
                borderBottom: `2px solid ${tab === key ? 'var(--accent)' : 'transparent'}`,
                background: 'transparent',
                padding: '8px 14px',
                fontSize: 13,
                fontWeight: tab === key ? 700 : 500,
                color: tab === key ? 'var(--text)' : 'var(--text-muted)',
                cursor: 'pointer',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* phase-template-preview-company-profile — "Preview as" selector +
            the shared mode banner, heading BOTH tabs. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 20px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <label
            htmlFor="template-preview-as"
            style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}
          >
            Preview as
          </label>
          <select
            id="template-preview-as"
            data-testid="template-preview-as-select"
            value={activeProfileId ?? ''}
            disabled={!canReadProfiles}
            onChange={(e) => setSelectedProfileId(e.target.value)}
            style={{
              fontSize: 13,
              padding: '4px 8px',
              borderRadius: 6,
              border: '1px solid var(--border)',
              background: 'var(--surface)',
              color: 'var(--text)',
              maxWidth: 320,
            }}
          >
            <option value="">Sample data («PLACEHOLDER»)</option>
            {profiles.map((p) => (
              <option key={p.Id} value={p.Id}>
                {p.CompanyName}
              </option>
            ))}
          </select>
          {!permissions.loading && !canReadProfiles && <span style={{ fontSize: 12 }}>Company-profile access is needed to preview real company values. Sample preview remains available.</span>}
          {profileError && <span role="alert">{profileError} <button type="button" onClick={() => setProfileReload(n => n + 1)}>Retry profiles</button></span>}
        </div>
        <p
          data-testid="template-preview-banner"
          style={{
            margin: 0,
            padding: '10px 20px',
            fontSize: 12,
            color: 'var(--amber, #b45309)',
            background: 'var(--amber-bg, #fffbeb)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          {bannerText}
        </p>

        {tab === 'pdf' && (
          <div
            data-testid="template-preview-pdf-pane"
            // phase-ui-preview-modal-pdf-height-resize — pure flex fill, no
            // fixed pixel height: minHeight 0 (not 380) is what lets a flex
            // child actually SHRINK-AND-GROW with the modal body. The old
            // minHeight was the "short fixed-height box" Sharon saw.
            style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
          >
            <div style={{ flex: 1, background: '#f3f4f6', minHeight: 0 }}>
              {pdf.loading && (
                <div
                  data-testid="template-preview-pdf-loading"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    fontSize: 13,
                    color: 'var(--text-muted)',
                  }}
                >
                  Rendering preview…
                </div>
              )}
              {!pdf.loading && pdf.error !== null && (
                <div
                  role="alert"
                  data-testid="template-preview-pdf-error"
                  style={{
                    margin: 20,
                    padding: 16,
                    border: '1px solid var(--red, #dc2626)',
                    borderRadius: 8,
                    background: 'var(--red-bg, #fef2f2)',
                    color: 'var(--red, #991b1b)',
                    fontSize: 13,
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>
                    The PDF preview could not be rendered
                  </div>
                  <div style={{ marginBottom: 12 }}>{pdf.error}</div>
                  <button
                    type="button"
                    onClick={pdf.retry}
                    data-testid="template-preview-pdf-retry"
                    style={{
                      border: '1px solid var(--red, #dc2626)',
                      borderRadius: 6,
                      background: 'transparent',
                      color: 'var(--red, #991b1b)',
                      padding: '5px 14px',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Retry
                  </button>
                </div>
              )}
              {!pdf.loading && pdf.error === null && pdf.url !== null && (
                <iframe
                  src={pdf.url}
                  title={`${template.Name} — PDF preview`}
                  data-testid="template-preview-pdf-frame"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                />
              )}
            </div>
          </div>
        )}

        <div
          style={{
            padding: 20,
            overflowY: 'auto',
            flex: 1,
            minHeight: 0,
            display: tab === 'text' ? 'block' : 'none',
          }}
        >
          {template.Description && (
            <p style={{ margin: '0 0 16px 0', fontSize: 13, color: 'var(--text-muted)' }}>
              {template.Description}
            </p>
          )}
          {/* phase-template-preview-company-profile — sample mode renders the
              template client-side («PLACEHOLDER» via renderTemplatePreview,
              unchanged); a chosen profile shows the server-substituted body
              (same resolver the PDF uses). */}
          {activeProfileId && textPreview.loading ? (
            <div data-testid="template-preview-text-loading" style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              Rendering preview…
            </div>
          ) : activeProfileId && textPreview.error !== null ? (
            <div
              role="alert"
              data-testid="template-preview-text-error"
              style={{ fontSize: 13, color: 'var(--red, #991b1b)' }}
            >
              {textPreview.error}
            </div>
          ) : (
            <pre
              data-testid="template-preview-body"
              style={{
                margin: 0,
                padding: 16,
                background: 'var(--surface2, #f8fafc)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                fontSize: 12.5,
                lineHeight: 1.55,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {activeProfileId && textPreview.text !== null
                ? textPreview.text
                : renderTemplatePreview(template.Body)}
            </pre>
          )}
        </div>

        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            data-testid="template-preview-close"
            style={{
              background: 'transparent',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: '6px 16px',
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            Close
          </button>
        </div>

        {/* phase-ui-preview-modal-pdf-height-resize — bottom-right resize
            grip; drag to resize between 480×420 and near-full-screen. */}
        <div
          data-testid="template-preview-resize-grip"
          aria-hidden="true"
          {...drag.resizeHandleProps}
          style={{ ...RESIZE_GRIP_STYLE, ...drag.resizeHandleProps.style }}
        />
      </div>
    </div>
  );
}
